/**
 * Hilfsfunktionen fuer das Parsen von iFrame URL-Parametern.
 *
 * Urspruenglich inline in src/index.tsx definiert, hierher extrahiert
 * fuer bessere Testbarkeit. Die Funktionen werden weiterhin von index.tsx importiert.
 */

/**
 * Parst URL-Parameter aus window.location.search UND window.location.hash
 * und gibt ein Key-Value-Objekt zurueck.
 *
 * Basiert auf URLSearchParams statt manuellem String-Split (Issue #73):
 * - Werte mit "=" bleiben vollstaendig erhalten (z. B. parentUrl mit ?fbclid=XYZ)
 * - Prozent-Encoding wird vollstaendig dekodiert (Umlaute, %20, encodierte URLs)
 * - "+" wird als Leerzeichen interpretiert (wie bisher)
 *
 * Der Hash wird IMMER zusaetzlich geparst (search-Parameter haben Vorrang):
 * - Hash-Routing: /#/?key=value → Parameter stehen nach dem "?"
 * - Alt-Embeds haengen parentUrl unencodiert an. Enthaelt die Eltern-URL ein "#",
 *   verschiebt der Browser alle folgenden Parameter ins Fragment
 *   (#anker&courseTypeIds=…) — von dort holen wir sie zurueck.
 *
 * Innerhalb einer Quelle gewinnt bei doppelten Keys der LETZTE Wert (Issue #78):
 * Im Hash steht zuerst das Fragment der Eltern-Seite und erst danach die vom
 * Embed angehaengte Konfiguration (alle Templates setzen parentUrl an Position 1).
 * Ein query-artiges Eltern-Fragment (#anker?courseTypeIds=…) darf die explizite
 * iFrame-Konfiguration also nicht ueberschreiben.
 *
 * @param _paramNames Historische Signatur; wird (wie bisher) nicht gefiltert —
 *                    alle gefundenen Parameter werden zurueckgegeben.
 */
export const getParamObject = (_paramNames: string[]): { [key: string]: string } => {
    try {
        const collect = (query: string): { [key: string]: string } => {
            const pairs: { [key: string]: string } = {}
            new URLSearchParams(query).forEach((value, key) => {
                // Anfuehrungszeichen entfernen (frueher: %22-Stripping in cleanUp)
                const cleanValue = value.replaceAll('"', "")
                if (!key || !cleanValue) return
                pairs[key] = cleanValue
            })
            return pairs
        }

        // Hash: nach "?" (Hash-Routing) bzw. komplett (abgeschnittene Alt-Embed-Parameter)
        const hash = window.location.hash
        const queryStart = hash.indexOf("?")
        const hashQuery = queryStart >= 0 ? hash.substring(queryStart + 1) : hash.substring(1)

        // Normale Query-Parameter (?key=value) gewinnen gegen gleichnamige Hash-Werte
        return { ...collect(hashQuery), ...collect(window.location.search) }
    } catch (error) {
        console.error(error)
        return {}
    }
}
